import { EventEmitter } from '../../utils/event-emitter.js';
import { BaseService } from '../base-service.js';
import { tokenProvider } from '../auth/token-provider.js';
import { fileProcessor } from '../file/file-processor.js';
import { getVersionedApiUrl } from '../../config/api-config.js';

// Define supported file types as constants
export const SUPPORTED_FILE_EXTENSIONS = {
  // Base types
  '.pdf': true,
  // Documents and presentations
  '.doc': true, '.docx': true, '.ppt': true, '.pptx': true,
  '.txt': true, '.rtf': true, '.odt': true, '.ods': true,
  '.odp': true, '.pages': true, '.numbers': true,
  '.key': true, '.md': true,
  // Spreadsheets
  '.xlsx': true, '.xls': true, '.csv': true
};

// Define tabular file types
export const TABULAR_FILE_EXTENSIONS = {
  '.xlsx': true, '.xls': true, '.csv': true
};

export class DocumentService extends BaseService {
  constructor() {
    super();
    this.basePath = '/documents';
    // BaseService already configures baseUrl via api-config; avoid overriding here
    this.events = new EventEmitter();
    this.documents = new Map();
    this._listCache = new Map(); // key -> { data, total, etag, ts }
    this._searchCache = new Map(); // key -> { results, etag, ts }
    this._searchAbortController = null;

    // Listen for FileProcessor events
    fileProcessor.on('processingError', ({ file, error }) => {
      this.emit('document-error', { error });
    });
  }

  // Delegate event emitter methods
  on(event, callback) {
    return this.events.on(event, callback);
  }

  off(event, callback) {
    return this.events.off(event, callback);
  }

  emit(event, data) {
    return this.events.emit(event, data);
  }

  getSessionInfo() {
    const sessionData = tokenProvider.getSessionData();
    if (!sessionData?.orgId || !sessionData?.userData?.id) {
      throw new Error('Missing required session data');
    }
    return { orgId: sessionData.orgId, userId: sessionData.userData.id };
  }

  async listDocuments(options = {}) {
    const { orgId, userId } = this.getSessionInfo();
    const params = new URLSearchParams();
    if (typeof options.limit === 'number' && options.limit > 0) params.set('limit', String(options.limit));
    if (typeof options.offset === 'number' && options.offset >= 0) params.set('offset', String(options.offset));
    if (options.sortBy) params.set('sortBy', String(options.sortBy));
    if (options.sortDirection) params.set('sortDirection', String(options.sortDirection));
    const qs = params.toString();
    const path = `/${orgId}/users/${userId}${qs ? `?${qs}` : ''}`;
    const cacheKey = `list:${orgId}:${userId}:${qs || 'default'}`;
    try {
      const cached = this._listCache.get(cacheKey);
      const headers = {};
      if (cached?.etag) headers['If-None-Match'] = cached.etag;
      if (cached?.lastModified) headers['If-Modified-Since'] = cached.lastModified;
      if (options.cacheMaxAgeSeconds) headers['Cache-Control'] = `max-age=${options.cacheMaxAgeSeconds}`;

      const resp = await this.get(path, {}, { headers, returnResponse: true, allowNotModified: true });
      if (resp && resp.status === 304 && cached) {
        // Serve from cache
        return { data: cached.data, total: cached.total };
      }

      // Parse fresh response
      const payload = await resp.json().catch(() => ({}));
      const documents = Array.isArray(payload.data) ? payload.data : (payload.data?.data || []);
      const total = typeof payload.total === 'number' ? payload.total : (payload.data?.total ?? undefined);

      documents.forEach(doc => this.documents.set(doc.id, doc));

      // Store ETag + cache
      const etag = resp.headers.get('ETag');
      const lastModified = resp.headers.get('Last-Modified');
      this._listCache.set(cacheKey, { data: documents, total, etag, lastModified, ts: Date.now() });

      return { data: documents, total };
    } catch (error) {
      console.error('Error listing documents:', error);
      // Fallback to cache on network errors
      const cached = this._listCache.get(cacheKey);
      if (cached) {
        return { data: cached.data, total: cached.total };
      }
      throw error;
    }
  }

  async uploadDocument(file, metadata = {}, options = {}) {
    try {
      const { orgId, userId } = this.getSessionInfo();

      // Validate file type
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      if (!(extension in SUPPORTED_FILE_EXTENSIONS)) {
        const error = new Error('Unsupported file type. Please upload a supported document type.');
        this.emit('document-error', { error });
        throw error;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', metadata.title || file.name);
      if (metadata.description) {
        formData.append('description', metadata.description);
      }
      
      // ❌ REMOVED: Gemini auto-selection (back to LlamaParse default)
      if (options.parseMode) {
        formData.append('parse_mode', options.parseMode);
        console.log(`[DocumentService] Using parse mode: ${options.parseMode}`);
      }
      
      // Support additional processing options
      if (options.extractLayout) {
        formData.append('extract_layout', 'true');
      }

      // Get headers without content-type (browser will set it with boundary)
      const token = tokenProvider.getToken();
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      // Upload the document
      const response = await fetch(`${this.baseUrl}${this.basePath}/${orgId}/users/${userId}/upload`, {
        method: 'POST',
        headers,
        body: formData
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Upload failed');
      }

      const result = await response.json();
      
      // Create a document object from the response
      const document = {
        id: result.data.document_id,
        title: file.name,
        mimeType: file.type,
        size: file.size,
        status: 'pending',
        createdAt: new Date().toISOString(),
        metadata: { ...metadata }
      };

      this.documents.set(document.id, document);
      
      // Start real-time DO status monitoring
      this.startDOStatusMonitoring(document.id);
      
      return document;
      
    } catch (error) {
      console.error('Error uploading document:', error);
      this.emit('document-error', { 
        error,
        file: {
          name: file.name,
          type: file.type,
          size: file.size
        }
      });
      throw error;
    }
  }

  /**
   * Upload a tabular file (CSV, XLS, XLSX)
   * @param {File} file - The file object to upload
   * @param {Object} metadata - Optional metadata for the document
   * @param {Object} options - Additional processing options
   * @param {boolean} options.preserveTables - Whether to preserve complete table structures (default: false)
   * @param {boolean} options.extractSubtables - Whether to extract subtables from sheets (default: true)
   * @param {boolean} options.continuousMode - Whether to use continuous mode processing (default: true)
   * @returns {Promise<Object>} - The uploaded document
   */
  async uploadTabular(file, metadata = {}, options = {}) {
    try {
      const { orgId, userId } = this.getSessionInfo();

      // Validate file type
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      if (!(extension in TABULAR_FILE_EXTENSIONS)) {
        const error = new Error('Unsupported tabular file type. Please upload a CSV, XLS, or XLSX file.');
        this.emit('document-error', { error });
        throw error;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', metadata.title || file.name);
      if (metadata.description) {
        formData.append('description', metadata.description);
      }
      
      // ❌ REMOVED: Gemini auto-selection for tabular (back to LlamaParse default)
      if (options.parseMode) {
        formData.append('parse_mode', options.parseMode);
        console.log(`[DocumentService] Using parse mode for tabular: ${options.parseMode}`);
      }
      
      // Add processing options
      if (options.preserveTables === true) {
        formData.append('preserve_tables', 'true');
      }
      
      if (options.extractSubtables === false) {
        formData.append('extract_subtables', 'false');
      }
      
      if (options.continuousMode === false) {
        formData.append('continuous_mode', 'false');
      }

      // Get headers without content-type (browser will set it with boundary)
      const token = tokenProvider.getToken();
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      // Upload the tabular document using the specific endpoint
      const response = await fetch(`${this.baseUrl}${this.basePath}/${orgId}/users/${userId}/upload-tabular`, {
        method: 'POST',
        headers,
        body: formData
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Tabular file upload failed');
      }

      const result = await response.json();
      
      // Create a document object from the response
      const document = {
        id: result.data.document_id,
        title: file.name,
        mimeType: file.type,
        size: file.size,
        status: 'pending',
        createdAt: new Date().toISOString(),
        metadata: { ...metadata }
      };

      this.documents.set(document.id, document);
      
      // Start real-time DO status monitoring
      this.startDOStatusMonitoring(document.id);
      
      return document;
      
    } catch (error) {
      console.error('Error uploading tabular document:', error);
      this.emit('document-error', { 
        error,
        file: {
          name: file.name,
          type: file.type,
          size: file.size
        }
      });
      throw error;
    }
  }

  async pollDocumentStatus(documentId, interval = 5000, maxAttempts = 60) {
    let attempts = 0;
    
    const checkStatus = async () => {
      try {
        const status = await fileProcessor.getDocumentStatus(documentId);
        
        // If we get a status response, update our document
        if (status) {
          const document = await this.getDocument(documentId);
          document.status = status;
          this.documents.set(document.id, document);
          
          if (status === 'processed') {
            this.emit('document-processed', { document });
            return;
          }
          
          if (status === 'error') {
            this.emit('document-error', { 
              error: new Error('Document processing failed') 
            });
            return;
          }
        }
        
        if (attempts++ < maxAttempts) {
          setTimeout(checkStatus, interval);
        } else {
          this.emit('document-error', {
            error: new Error('Document processing timed out')
          });
        }
      } catch (error) {
        console.error('Error polling document status:', error);
        this.emit('document-error', { error });
      }
    };

    checkStatus();
  }

  async getDocument(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.get(`/${orgId}/users/${userId}/document/${documentId}`);
      const document = response.data || {};
      this.documents.set(document.id, document);
      return document;
    } catch (error) {
      console.error('Error getting document:', error);
      throw error;
    }
  }

  async deleteDocument(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      await this.delete(`/${orgId}/users/${userId}/document/${documentId}`);
      this.documents.delete(documentId);
      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  async getDocumentChunks(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.get(`/${orgId}/users/${userId}/document/${documentId}/chunks`);
      // Return all chunks (including visual/image chunks). Consumers decide what to render.
      return response.data || [];
    } catch (error) {
      console.error('Error getting document chunks:', error);
      throw error;
    }
  }

  async getDocumentWithChunks(documentId) {
    try {
      const document = await this.getDocument(documentId);
      const chunks = await this.getDocumentChunks(documentId);
      return { ...document, chunks };
    } catch (error) {
      console.error('Error getting document with chunks:', error);
      throw error;
    }
  }

  async getParseJob(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      
      // Call the API to get the parse job information
      const response = await this.get(`/${orgId}/users/${userId}/documents/${documentId}/parse-job`);
      return response;
    } catch (error) {
      console.error('Error getting parse job:', error);
      // Return null for any error, as we'll fall back to checking document status directly
      return null;
    }
  }

  async reprocessDocument(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const token = tokenProvider.getToken();
      
      // Call the API to trigger reprocessing
      const response = await fetch(`${this.baseUrl}${this.basePath}/${orgId}/users/${userId}/document/${documentId}/reprocess`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Reprocessing failed');
      }
      
      const result = await response.json();

      // Update document status
      if (this.documents.has(documentId)) {
        const document = this.documents.get(documentId);
        document.status = 'processing';
        this.documents.set(documentId, document);
      }
      
      return result;
    } catch (error) {
      console.error('Error reprocessing document:', error);
      throw error;
    }
  }

  async retryWorkflow(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      
      // Call the API to trigger workflow retry
      const response = await this.post(`/${orgId}/users/${userId}/document/${documentId}/retry-workflow`);
      
      // Update document status
      if (this.documents.has(documentId)) {
        const document = this.documents.get(documentId);
        document.status = 'processing';
        this.documents.set(documentId, document);
      }
      
      return response;
    } catch (error) {
      console.error('Error retrying workflow:', error);
      throw error;
    }
  }

  async refreshDocument(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      
      // First try to get the document directly
      try {
        const document = await this.getDocument(documentId);
        return { 
          status: document.status,
          document 
        };
      } catch (error) {
        console.log('Could not refresh document status directly:', error);
        // Fall back to checking the parse job
        const parseJob = await this.getParseJob(documentId);
        if (parseJob) {
          return {
            status: parseJob.status,
            parseJob
          };
        }
        throw error;
      }
    } catch (error) {
      console.error('Error refreshing document:', error);
      throw error;
    }
  }

  searchDocuments(query) {
    query = query.toLowerCase();
    return Array.from(this.documents.values()).filter(doc => 
      doc.title.toLowerCase().includes(query) ||
      (doc.description && doc.description.toLowerCase().includes(query))
    );
  }

  async searchDocuments(query, options = {}) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.post(`/${orgId}/users/${userId}/search`, {
        query,
        limit: options.limit || 10
      });
      return response.data || [];
    } catch (error) {
      console.error('Error searching documents:', error);
      throw error;
    }
  }

  /**
   * Abortable realtime search for documents
   * Cancels any in-flight search before issuing the next one
   */
  async searchDocumentsRealtime(query, options = {}) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      if (this._searchAbortController) {
        try { this._searchAbortController.abort(); } catch {}
      }
      this._searchAbortController = new AbortController();
      const key = `search:${orgId}:${userId}:${options.limit || 10}:${query}`;
      const cached = this._searchCache.get(key);
      const headers = {};
      if (cached?.etag) headers['If-None-Match'] = cached.etag;
      if (cached?.lastModified) headers['If-Modified-Since'] = cached.lastModified;
      if (options.cacheMaxAgeSeconds) headers['Cache-Control'] = `max-age=${options.cacheMaxAgeSeconds}`;

      const resp = await this.post(
        `/${orgId}/users/${userId}/search`,
        {
          query,
          limit: options.limit || 10
        },
        { signal: this._searchAbortController.signal, headers, returnResponse: true, allowNotModified: true }
      );

      if (resp && resp.status === 304 && cached) {
        return cached.results;
      }

      const payload = await resp.json().catch(() => ({}));
      const results = Array.isArray(payload?.data) ? payload.data : (payload || []);
      const etag = resp.headers.get('ETag');
      const lastModified = resp.headers.get('Last-Modified');
      this._searchCache.set(key, { results, etag, lastModified, ts: Date.now() });
      return results;
    } catch (error) {
      // Swallow abort errors to avoid noisy UI
      if (error && (error.name === 'AbortError' || /aborted|AbortError|timed out/i.test(error.message || ''))) {
        // try local fallback to keep UX smooth
        const local = this._localFilterDocuments(query, options.limit || 10);
        return local;
      }
      // Backend failed. Try graceful fallbacks: cache -> name search -> local filter
      try {
        const { orgId, userId } = this.getSessionInfo();
        const key = `search:${orgId}:${userId}:${options.limit || 10}:${query}`;
        const cached = this._searchCache.get(key);
        if (cached && Array.isArray(cached.results) && cached.results.length) return cached.results;
      } catch {}
      try {
        const byName = await this.findDocumentByName(query, { threshold: 0.6 });
        if (Array.isArray(byName) && byName.length) return byName.slice(0, options.limit || 10);
      } catch {}
      const local = this._localFilterDocuments(query, options.limit || 10);
      if (local.length) return local;
      console.error('Error searching documents (realtime):', error);
      throw error;
    } finally {
      this._searchAbortController = null;
    }
  }

  _localFilterDocuments(query, limit = 10) {
    if (!query || !this.documents || this.documents.size === 0) return [];
    const q = String(query).toLowerCase();
    const arr = Array.from(this.documents.values());
    const scored = arr
      .map(d => {
        const title = (d.title || '').toLowerCase();
        const desc = (d.description || '').toLowerCase();
        const summary = (d.summary || '').toLowerCase();
        let score = 0;
        if (title.includes(q)) score += 2;
        if (desc.includes(q)) score += 1;
        if (summary.includes(q)) score += 1;
        return { d, score };
      })
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map(x => x.d);
    return scored;
  }

  cancelSearch() {
    if (this._searchAbortController) {
      try { this._searchAbortController.abort(); } catch {}
      this._searchAbortController = null;
    }
  }

  /**
   * Title-only realtime search (abortable). Uses backend name search then local fallback.
   */
  async searchTitlesRealtime(query, options = {}) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      if (this._searchAbortController) {
        try { this._searchAbortController.abort(); } catch {}
      }
      this._searchAbortController = new AbortController();

      // Prefer exact endpoint designed for name/title
      const resp = await this.post(
        `/${orgId}/users/${userId}/search-by-name`,
        {
          query,
          includeChunks: false,
          threshold: options.threshold || 0.6
        },
        { signal: this._searchAbortController.signal }
      );
      const serverResults = Array.isArray(resp?.data) ? resp.data : (resp || []);
      // Merge with local filter over summary/description to include text beyond titles
      const local = this._localFilterDocuments(query, options.limit || 10);
      const mergedMap = new Map();
      [...serverResults, ...local].forEach(d => { if (d && d.id) mergedMap.set(d.id, d); });
      return Array.from(mergedMap.values()).slice(0, options.limit || 10);
    } catch (error) {
      if (error && (error.name === 'AbortError' || /aborted|AbortError/i.test(error.message || ''))) {
        return this._localFilterDocuments(query, options.limit || 10);
      }
      // Fallback to local filter on any error
      return this._localFilterDocuments(query, options.limit || 10);
    } finally {
      this._searchAbortController = null;
    }
  }

  async updateDocumentStatus(documentId, status) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.put(`/${orgId}/users/${userId}/document/${documentId}`, {
        status
      });
      return response.data;
    } catch (error) {
      console.error('Error updating document status:', error);
      throw error;
    }
  }

  async checkParseJob(documentId, jobId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.post(`/${orgId}/users/${userId}/documents/${documentId}/parse-job/check/${jobId}`);
      return response;
    } catch (error) {
      console.error('Error checking parse job:', error);
      this.emit('document-error', { error });
      throw error;
    }
  }

  async getDiagnosticInfo() {
    try {
      const { orgId, userId } = this.getSessionInfo();
      // return this.get(`/${orgId}/users/${userId}/documents/diagnostic`); // This prepends the basePath incorrectly

      // Use direct fetch to construct the correct path relative to the API root
      // const sessionData = tokenProvider.getSessionData(); // Get token directly
      const token = tokenProvider.getToken();
      if (!token) { // Check token directly
        throw new Error('Authentication token and API URL required for diagnostics.');
      }

      // Get API URL dynamically to avoid potential initialization issues
      const apiUrl = getVersionedApiUrl('v1');

      
      const url = `${apiUrl}/documents/${orgId}/users/${userId}/documents/diagnostic`; 
      console.log(`[DocumentService] Fetching diagnostics from: ${url}`);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('[DocumentService] Error response from diagnostic endpoint:', data);
        throw new Error(data.error || `Failed to fetch diagnostics (status: ${response.status})`);
      }

      console.log('[DocumentService] Received diagnostics:', data.data);
      return data.data; // Return just the data part

    } catch (error) {
      console.error('Error getting diagnostic info:', error);
      this.emit('document-error', { error });
      throw error;
    }
  }

  async findDocumentByName(name, options = {}) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.post(`/${orgId}/users/${userId}/search-by-name`, {
        query: name,
        includeChunks: !!options.includeChunks,
        threshold: options.threshold || 0.7
      });
      return response.data || [];
    } catch (error) {
      console.error('Error finding document by name:', error);
      throw error;
    }
  }

  async getChunkCounts() {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.get(`/${orgId}/users/${userId}/documents/chunks/count`);
      return response;
    } catch (error) {
      console.error('Error getting chunk counts:', error);
      this.emit('document-error', { error });
      throw error;
    }
  }

  /**
   * Search chunks with citations and page information
   * @param {string} query - Search query
   * @param {number} limit - Maximum number of results (default 10)
   * @returns {Promise<Array>} - Search results with citations
   */
  async searchChunksWithCitations(query, limit = 10) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.post(`/${orgId}/users/${userId}/chunks/search-with-citations`, {
        query,
        limit
      });
      return response.data || [];
    } catch (error) {
      console.error('Error searching chunks with citations:', error);
      throw error;
    }
  }

  /**
   * Search chunks with metadata filtering
   * @param {string} query - Search query
   * @param {Object} metadata - Metadata filters
   * @param {Object} options - Search options
   * @returns {Promise<Array>} - Filtered search results
   */
  async searchChunksWithMetadata(query, metadata, options = {}) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.post(`/${orgId}/users/${userId}/chunks/search-with-metadata`, {
        query,
        metadata,
        filter_strategy: options.filterStrategy || 'post',
        limit: options.limit || 10,
        similarity_threshold: options.similarityThreshold || 0
      });
      return response.data || [];
    } catch (error) {
      console.error('Error searching chunks with metadata:', error);
      throw error;
    }
  }

  /**
   * Search basic chunks
   * @param {string} query - Search query
   * @param {number} limit - Maximum number of results (default 10)
   * @returns {Promise<Array>} - Search results
   */
  async searchChunks(query, limit = 10) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.post(`/${orgId}/users/${userId}/chunks/search`, {
        query,
        limit
      });
      return response.data || [];
    } catch (error) {
      console.error('Error searching chunks:', error);
      throw error;
    }
  }

  /**
   * Update a document's metadata
   * @param {string} documentId - ID of the document to update
   * @param {object} updateData - Updated document data
   * @returns {Promise<object>} - Updated document
   */
  async updateDocument(documentId, updateData) {
    try {
      // Get token from tokenProvider 
      const token = tokenProvider.getToken();
      const { orgId, userId } = this.getSessionInfo();
      
      if (!token || !orgId || !userId) {
        throw new Error('Missing required session data');
      }

      // If this is a visibility update, use the dedicated endpoint
      if ('visibility' in updateData) {
        // Use the new visibility-specific endpoint
        const response = await fetch(`${this.baseUrl}${this.basePath}/${orgId}/users/${userId}/document/${documentId}/visibility`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ visibility: updateData.visibility })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to update document visibility: ${response.status}`);
        }

        const result = await response.json();
        
        // If response includes metadata about chunks updated, log it
        if (result.chunksUpdated) {
          console.log(`Document visibility updated: ${result.chunksUpdated} chunks affected`);
        }
        
        // Update local cache and emit event
        if (this.documents.has(documentId)) {
          const cachedDoc = this.documents.get(documentId);
          const updatedDoc = { ...cachedDoc, visibility: updateData.visibility };
          this.documents.set(documentId, updatedDoc);
          this.emit('document-updated', { document: updatedDoc });
          return updatedDoc;
        }
        
        return result.document || result;
      } else {
        // For other updates, use the existing general update endpoint
        const payload = { ...updateData };

        // Use the existing endpoint pattern
        const response = await fetch(`${this.baseUrl}${this.basePath}/${orgId}/users/${userId}/document/${documentId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to update document: ${response.status}`);
        }

        const updatedDocument = await response.json();
        this.emit('document-updated', { document: updatedDocument });
        
        return updatedDocument;
      }
    } catch (error) {
      console.error('Error updating document:', error);
      this.emit('document-error', { error });
      throw error;
    }
  }

  /**
   * Get specific document metadata by type
   * @param {string} documentId - Document ID
   * @param {string} metadataType - Type of metadata (e.g., 'pages', 'parse_result', 'layout')
   * @returns {Promise<Object>} - Document metadata
   */
  async getDocumentMetadata(documentId, metadataType) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.get(`/${orgId}/users/${userId}/document/${documentId}/metadata/${metadataType}`);
      return response.data || null;
    } catch (error) {
      console.error('Error getting document metadata:', error);
      throw error;
    }
  }

  /**
   * Get document pages information
   * @param {string} documentId - Document ID
   * @returns {Promise<Array>} - Document pages
   */
  async getDocumentPages(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.get(`/${orgId}/users/${userId}/document/${documentId}/pages`);
      return response.data || [];
    } catch (error) {
      console.error('Error getting document pages:', error);
      throw error;
    }
  }

  /**
   * Get document token statistics
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} - Token statistics
   */
  async getDocumentTokenStats(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.get(`/${orgId}/users/${userId}/document/${documentId}/token-stats`);
      return response.data || {};
    } catch (error) {
      console.error('Error getting document token stats:', error);
      throw error;
    }
  }

  /**
   * Recaption a single image chunk with optional guidance
   * @param {string} documentId
   * @param {string} chunkId
   * @param {object} options
   * @param {string} [options.guidance]
   * @param {string} [options.model]
   */
  async recaptionImageChunk(documentId, chunkId, options = {}) {
    try {
      const token = tokenProvider.getToken();
      const { orgId, userId } = this.getSessionInfo();
      const response = await fetch(`${this.baseUrl}${this.basePath}/${orgId}/users/${userId}/document/${documentId}/chunks/${chunkId}/recaption`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ guidance: options.guidance, model: options.model })
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error || 'Failed to recaption image');
      }
      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error recaptioning image chunk:', error);
      this.emit('document-error', { error });
      throw error;
    }
  }

  /**
   * Get failed jobs for the user
   * @returns {Promise<Array>} - Failed jobs
   */
  async getFailedJobs() {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.get(`/${orgId}/users/${userId}/failed-jobs`);
      return response.data || [];
    } catch (error) {
      console.error('Error getting failed jobs:', error);
      throw error;
    }
  }

  /**
   * Retry a failed job
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} - Retry result
   */
  async retryFailedJob(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.post(`/documents/${orgId}/users/${userId}/retry-job/${documentId}`);
      return response.data || {};
    } catch (error) {
      console.error('Error retrying failed job:', error);
      throw error;
    }
  }

  /**
   * Test table chunking on content
   * @param {string} content - Content to test chunking on
   * @param {Object} options - Processing options
   * @returns {Promise<Object>} - Chunking test results
   */
  async testTableChunking(content, options = {}) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.post(`/${orgId}/users/${userId}/test-table-chunking`, {
        content,
        ...options
      });
      return response.data || {};
    } catch (error) {
      console.error('Error testing table chunking:', error);
      throw error;
    }
  }

  /**
   * Test embedding optimizer
   * @param {Object} options - Optimizer options
   * @returns {Promise<Object>} - Optimizer test results
   */
  async testEmbeddingOptimizer(options = {}) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.post(`/${orgId}/users/${userId}/test-optimizer`, {
        content: options.content || 'Test content for embedding optimization',
        max_chunk_size: options.maxChunkSize || 8000,
        chunk_overlap: options.chunkOverlap || 200,
        token_limit: options.tokenLimit || 150000
      });
      return response.data || {};
    } catch (error) {
      console.error('Error testing embedding optimizer:', error);
      throw error;
    }
  }

  /**
   * Process document content directly (for testing)
   * @param {string} documentId - Document ID
   * @param {Object} options - Processing options
   * @returns {Promise<Object>} - Processing result
   */
  async processDocumentContent(documentId, options = {}) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const response = await this.post(`/${orgId}/users/${userId}/process/${documentId}`, options);
      return response.data || {};
    } catch (error) {
      console.error('Error processing document content:', error);
      throw error;
    }
  }

  // ==========================================
  // DocumentProcessingDO Integration Methods
  // ==========================================

  /**
   * Start real-time status monitoring using DocumentProcessingDO
   * @param {string} documentId - Document ID to monitor
   */
  async startDOStatusMonitoring(documentId) {
    const pollStatus = async () => {
      try {
        const { orgId, userId } = this.getSessionInfo();
        const token = tokenProvider.getToken();
        
        const response = await fetch(
          `${this.baseUrl}${this.basePath}/${orgId}/users/${userId}/document/${documentId}/processing-status`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.ok) {
          const status = await response.json();
          
          // Update local document cache
          if (this.documents.has(documentId)) {
            const document = this.documents.get(documentId);
            document.status = status.status || document.status;
            document.processingInfo = {
              lastPolledAt: status.lastPolledAt,
              retryCount: status.retryCount,
              maxRetries: status.maxRetries,
              error: status.error
            };
            this.documents.set(documentId, document);
          }
          
          // Emit status update event
          this.emit('do-status-update', { 
            documentId, 
            status: status.status,
            processingInfo: status,
            timestamp: new Date().toISOString()
          });
          
          // Continue polling if still processing
          if (['polling', 'processing', 'pending'].includes(status.status)) {
            setTimeout(pollStatus, 5000); // Poll every 5 seconds
          } else if (status.status === 'completed') {
            this.emit('document-processed', { 
              documentId,
              document: this.documents.get(documentId)
            });
          } else if (status.status === 'failed') {
            this.emit('document-error', { 
              error: new Error(status.error || 'Document processing failed'),
              documentId
            });
          }
        } else if (response.status === 404) {
          // DO not found - document might not be using DO tracking
          console.log(`No DO tracking found for document ${documentId}`);
        }
      } catch (error) {
        console.error('DO status polling error:', error);
        // Retry in 10 seconds on error
        setTimeout(pollStatus, 10000);
      }
    };
    
    // Start polling
    pollStatus();
  }

  /**
   * Get real-time processing status from DocumentProcessingDO
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} Processing status
   */
  async getDOProcessingStatus(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const token = tokenProvider.getToken();
      
      const response = await fetch(
        `${this.baseUrl}${this.basePath}/${orgId}/users/${userId}/document/${documentId}/processing-status`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to get processing status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting DO processing status:', error);
      throw error;
    }
  }

  /**
   * Instantly recover a stuck document using DocumentProcessingDO
   * @param {string} documentId - Document ID to recover
   * @returns {Promise<Object>} Recovery result
   */
  async recoverStuckDocument(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const token = tokenProvider.getToken();
      
      const response = await fetch(
        `${this.baseUrl}${this.basePath}/${orgId}/users/${userId}/document/${documentId}/recover`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Recovery failed');
      }
      
      const result = await response.json();
      
      // Start monitoring the recovered document
      this.startDOStatusMonitoring(documentId);
      
      // Emit recovery event
      this.emit('document-recovery-started', {
        documentId,
        message: 'Document recovery started - polling every 30 seconds',
        result
      });
      
      return result;
    } catch (error) {
      console.error('Error recovering stuck document:', error);
      this.emit('document-error', { error, documentId });
      throw error;
    }
  }

  /**
   * Manually trigger immediate polling for a document
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} Poll result
   */
  async manualPollDocument(documentId) {
    try {
      const { orgId, userId } = this.getSessionInfo();
      const token = tokenProvider.getToken();
      
      const response = await fetch(
        `${this.baseUrl}${this.basePath}/${orgId}/users/${userId}/document/${documentId}/poll-now`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error.message || 'Manual poll failed');
      }
      
      const result = await response.json();
      
      // Emit poll event
      this.emit('document-manual-poll', {
        documentId,
        result
      });
      
      return result;
    } catch (error) {
      console.error('Error manually polling document:', error);
      this.emit('document-error', { error, documentId });
      throw error;
    }
  }

  /**
   * Check if a document is stuck and can be recovered
   * @param {string} documentId - Document ID
   * @returns {Promise<Object>} Stuck status and recovery options
   */
  async checkStuckDocument(documentId) {
    try {
      const document = await this.getDocument(documentId);
      const now = new Date();
      const createdAt = new Date(document.createdAt);
      const minutesStuck = Math.floor((now - createdAt) / (1000 * 60));
      
      const isStuck = document.status === 'pending' && minutesStuck > 10;
      
      let doStatus = null;
      try {
        doStatus = await this.getDOProcessingStatus(documentId);
      } catch (error) {
        // DO might not exist for this document
      }
      
      return {
        documentId,
        isStuck,
        minutesStuck,
        status: document.status,
        doStatus: doStatus?.status || 'no_do',
        canRecover: isStuck,
        recommendation: isStuck ? 
          'Document appears stuck - click recover to start immediate polling' :
          'Document is processing normally'
      };
    } catch (error) {
      console.error('Error checking stuck document:', error);
      throw error;
    }
  }
}

export const documentService = new DocumentService(); 